import { useEffect, useState } from "react";
import { Link, NavLink, useLocation } from "react-router-dom";
import { Moon, Sun, Menu, X, Wheat } from "lucide-react";
import { Button } from "@/components/ui/button";
import { useTheme } from "@/components/ThemeProvider";
import { LOGO_MAIN } from "@/data/content";

const NAV_ITEMS = [
  { to: "/", label: "Acasă" },
  { to: "/despre-noi", label: "Despre noi" },
  { to: "/produse", label: "Produse" },
  { to: "/horeca", label: "HoReCa" },
  { to: "/inovatie", label: "Inovație" },
  { to: "/blog", label: "Blog" },
  { to: "/contact", label: "Contact" },
];

export default function Header() {
  const { theme, toggle } = useTheme();
  const [open, setOpen] = useState(false);
  const [scrolled, setScrolled] = useState(false);
  const { pathname } = useLocation();

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 8);
    onScroll();
    window.addEventListener("scroll", onScroll, { passive: true });
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  useEffect(() => {
    setOpen(false);
  }, [pathname]);

  return (
    <header
      data-testid="site-header"
      className={`fixed top-0 inset-x-0 z-40 transition-all duration-300 ${scrolled ? "glass" : "bg-transparent"}`}
    >
      <div className="max-w-7xl mx-auto container-px h-16 md:h-20 flex items-center justify-between">
        <Link to="/" data-testid="header-logo" className="flex items-center gap-3 group">
          <img src={LOGO_MAIN} alt="DOTAPAN" className="h-10 md:h-12 w-auto object-contain" />
          <div className="hidden sm:flex flex-col leading-tight">
            <span className="font-display text-base md:text-lg tracking-wide text-text">DOTAPAN</span>
            <span className="text-[10px] uppercase tracking-[0.25em] text-gold">Din 2019</span>
          </div>
        </Link>

        <nav className="hidden lg:flex items-center gap-1">
          {NAV_ITEMS.map((item) => (
            <NavLink
              key={item.to}
              to={item.to}
              end={item.to === "/"}
              data-testid={`nav-link-${item.to.replace("/", "") || "home"}`}
              className={({ isActive }) =>
                `relative px-4 py-2 text-sm font-medium tracking-wide transition-colors ${
                  isActive ? "text-gold" : "text-text-soft hover:text-text"
                }`
              }
            >
              {({ isActive }) => (
                <>
                  {item.label}
                  {isActive && (
                    <span className="absolute left-1/2 -translate-x-1/2 -bottom-1 h-[2px] w-6 bg-gold rounded-full" />
                  )}
                </>
              )}
            </NavLink>
          ))}
        </nav>

        <div className="flex items-center gap-2">
          <Link to="/contact" className="hidden md:inline-flex" data-testid="header-cta-link">
            <Button className="btn-gold rounded-full px-5 h-10" data-testid="header-cta">
              Cere ofertă
            </Button>
          </Link>
          <button
            onClick={toggle}
            aria-label="Toggle theme"
            data-testid="theme-toggle"
            className="h-10 w-10 rounded-full border border-border flex items-center justify-center hover:border-gold transition-colors"
          >
            {theme === "dark" ? <Sun className="h-4 w-4 text-gold" /> : <Moon className="h-4 w-4 text-text" />}
          </button>
          <button
            onClick={() => setOpen((o) => !o)}
            aria-label="Open menu"
            data-testid="mobile-menu-toggle"
            className="lg:hidden h-10 w-10 rounded-full border border-border flex items-center justify-center"
          >
            {open ? <X className="h-4 w-4" /> : <Menu className="h-4 w-4" />}
          </button>
        </div>
      </div>

      {/* Mobile menu */}
      {open && (
        <div className="lg:hidden glass border-t border-border" data-testid="mobile-menu">
          <div className="max-w-7xl mx-auto container-px py-6 flex flex-col gap-1">
            {NAV_ITEMS.map((item) => (
              <NavLink
                key={item.to}
                to={item.to}
                end={item.to === "/"}
                data-testid={`mobile-nav-${item.to.replace("/", "") || "home"}`}
                className={({ isActive }) =>
                  `px-3 py-3 rounded-lg text-base font-medium ${
                    isActive ? "text-gold bg-surface-2" : "text-text-soft hover:bg-surface-2"
                  }`
                }
              >
                {item.label}
              </NavLink>
            ))}
            <Link to="/contact" className="mt-2">
              <Button className="btn-gold w-full rounded-full h-11" data-testid="mobile-header-cta">
                Cere ofertă HoReCa
              </Button>
            </Link>
          </div>
        </div>
      )}
    </header>
  );
}
